import React from 'react';

import { useTranslation } from 'react-i18next';

interface FileArtifactCardProps {
  url: string;
  fileName: string;
}

function getExt(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

function extLabel(ext: string): string {
  return ext ? ext.toUpperCase() : 'FILE';
}

// Neutral file icon — no aggressive colors
function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.75" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

export const FileArtifactCard: React.FC<FileArtifactCardProps> = ({ url, fileName }) => {
  const { t } = useTranslation('pleri/chat');
  const ext = getExt(fileName);
  const label = extLabel(ext);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group mt-1 flex w-full max-w-sm items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm transition-all duration-150 hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600"
      title={t('openFile', { fileName })}
    >
      {/* Neutral icon — gray, no color associations */}
      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800">
        <FileIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100">{fileName}</div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <span className="text-xs font-medium text-gray-400 dark:text-gray-500">{label}</span>
          <span className="text-xs text-gray-300 dark:text-gray-600">·</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">{t('clickToOpen')}</span>
        </div>
      </div>
      <svg
        className="h-4 w-4 flex-shrink-0 text-gray-300 transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-gray-500 dark:text-gray-600 dark:group-hover:text-gray-400"
        fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
      </svg>
    </a>
  );
};
